/**
 * Écrans Téléphonie (Trunk SIP, Suivi de consommation Tel HD) : le back n'est pas
 * encore disponible pour la plupart des fonctions ci-dessous, qui renvoient donc les
 * données de la maquette au lieu d'appeler l'API. Le jour où le back existe, seule
 * l'implémentation change (un axios.get) — les hooks et les composants restent
 * identiques. `getTrunkNumeros` (liste des numéros), `getNumeroDetail` et
 * `updateNumero` (détail/modification d'un numéro) sont déjà branchées sur
 * l'API réelle. Pour `getTrunkNumeros`, les champs que l'endpoint liste ne
 * connaît pas (boîte vocale, appels entrants, limitations, contact, site)
 * restent sur leurs valeurs par défaut — ils sont réels sur `getNumeroDetail`.
 * `getTelephonieServices` (liste des services) appelle elle aussi l'API réelle
 * (`GET /api/services?type=TELEPHONY`, via `getServicesByClient`) : une
 * prestation marquée téléphonie côté CIS mais jamais provisionnée sur la
 * plateforme voix VTX (ex. service 563809) peut y apparaître alors que ses
 * endpoints api-telephonie répondent 404 — incohérence CIS/VTX à résoudre
 * côté données, pas une raison de masquer la liste réelle.
 */
import axios from 'axios';
import {
  AcdCall,
  AnnuaireContact,
  AnnuaireInterneEntry,
  AppelHistorique,
  AssignationClir,
  CallRedirectInfo,
  ConfigurationAntiSpamAppel,
  ConfigurationBoiteVocale,
  ConfigurationTrunkSip,
  ConsoCall,
  ConsoKpi,
  ContactToIp,
  EntreeBlacklistEntrant,
  FichierSon,
  ID_PLAGE_HORS_PLAGE,
  ID_PLAGE_VACANCES,
  IdentifiantsSip,
  Ivr,
  LocalisationSite,
  MaterielIp,
  ModeleMateriel,
  NumeroDetail,
  NumeroGlobal,
  PbxActiviteLigne,
  PbxGroupeAppel,
  PlageHoraireGroupe,
  SalleConference,
  SipsSrtpStatut,
  TicketAppelEntrant,
  TrunkNumero,
  TypeSon,
  UtilisateurNumeroCourtInvalide,
} from '../Components/Telephonie/TrunkSip/mocks';
import {InfoRow} from '../Components/Telephonie/TrunkSip/common';
import {ListService} from '../Types/service';
import {getServicesByClient} from './api';

/** Prestations téléphonie (CIS) rattachées au client connecté — même endpoint que `ServiceList`. */
const getTelephonieServices = async (): Promise<ListService[]> => getServicesByClient('TELEPHONY');

export interface SuiviConsommationTelHD {
  devise: string;
  consommations: {destination: string; duree: string; montant: string}[];
  total: {duree: string; montant: string};
  synthese: {
    montant: string;
    voix: string;
    data: string;
    messages: string;
    plafond: string;
    pourcentage: number;
  };
  notePreselection: string;
}

export type SuiviConsommationClassement = 'destination' | 'compte';

export interface TrunkSipConsommation {
  devise: string;
  kpis: ConsoKpi[];
  forfait: {dureeConsommee: string; montant: string; pourcentage: number};
  calls: ConsoCall[];
  acdCalls: AcdCall[];
}

interface ApiCallRecord {
  id: number;
  callDate: string;
  caller: string;
  callee: string;
  durationSeconds: number;
  amount: {amount: string; currency: string};
  destination: string;
  /**
   * Nom du contact provisionné sur le numéro appelant (Number.contact côté
   * api-telephonie), résolu uniquement par `getTrunkSipConsommation` —
   * absent sur le tableau paginé brut (`/reporting/{direction}`, `getCallsPage`),
   * qui ne fait pas cette résolution.
   */
  callerContactName?: string | null;
}

/** Enveloppe renvoyée par le back pour les endpoints de reporting téléphonie. */
interface ApiCallRecordsResponse {
  data: ApiCallRecord[];
  total: number;
}

/**
 * Un montant manquant/non numérique en amont (ex. `/reporting/{direction}`, non
 * protégé par un DTO côté back) ne doit pas transformer tout un total agrégé en
 * NaN via `Array.reduce`.
 *
 * Arrondi au centime dès la lecture : les agrégats (par destination, par numéro
 * appelant, total) somment ensuite tous la même valeur arrondie que celle affichée
 * par appel, évitant qu'un arrondi indépendant par sous-total diverge du total réel.
 */
const parseMontant = (value: string | undefined): number => {
  const montant = parseFloat(value ?? '');
  return Number.isFinite(montant) ? Math.round(montant * 100) / 100 : 0;
};

const toConsoCall = (record: ApiCallRecord, type: ConsoCall['type']): ConsoCall => ({
  date: record.callDate,
  appele: record.callee,
  nomAppelant: record.callerContactName ?? '—',
  numAppelant: record.caller,
  destination: record.destination,
  duree: record.durationSeconds,
  type,
  cout: parseMontant(record.amount?.amount),
});

const formatDuree = (totalSeconds: number): string => {
  const heures = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secondes = totalSeconds % 60;
  const parts = heures > 0 ? [`${String(heures).padStart(2, '0')}h`] : [];
  parts.push(`${String(minutes).padStart(2, '0')}mn`, `${String(secondes).padStart(2, '0')}s`);
  return parts.join(' ');
};

const formatDureeConsommee = (totalSeconds: number): string =>
  `${formatDuree(totalSeconds)} / illimité`;

/** `EUR` s'affiche avec son symbole, les autres devises (ex. `CHF`) avec leur code ISO. */
export const symboleDevise = (devise: string): string => (devise === 'EUR' ? '€' : devise);

const formatMontant = (montant: number, devise: string): string =>
  `${Math.round(montant).toLocaleString('fr-FR')} ${symboleDevise(devise)}`;

export interface ApiMoneyAmount {
  amount: string;
  currency: string;
}

export interface ApiFinancialLimits {
  globalLimit: ApiMoneyAmount;
  inaLimit?: ApiMoneyAmount;
  restrictionZone1: boolean;
  confirmSensitiveDestinations: boolean;
}

export interface ApiCallAuthorization {
  type: string;
  label: string;
  /** Indicatifs ou explications de la catégorie ; absent quand le legacy n'en portait aucun. */
  description?: string;
  allowed: boolean;
}

export interface ApiBudgetUsage {
  globalUsedPercent: number;
  globalEffective: ApiMoneyAmount;
  inaUsedPercent?: number;
  inaEffective?: ApiMoneyAmount;
}

/** Quels écrans optionnels de téléphonie s'appliquent à ce service. */
export interface ApiServiceCapabilities {
  outboundRestrictions: boolean;
  blockedCallers: boolean;
  ipFiltering: boolean;
  ipFilteringLocked: boolean;
}

/** Ressource Limitation d'api-telephonie (`GET`/`PUT /v1/service/{serviceId}/limitation`). */
export interface ApiLimitation {
  serviceId: number;
  country: string;
  lockedByProductLine: boolean;
  capabilities: ApiServiceCapabilities;
  financialLimits: ApiFinancialLimits;
  authorizations: ApiCallAuthorization[];
  usage: ApiBudgetUsage;
}

const getLimitation = async (serviceId: number): Promise<ApiLimitation> => {
  const response = await axios.get<ApiLimitation>(
    `/api/services/telephonie/${serviceId}/limitation`
  );
  return response.data;
};

/**
 * Remplace la limitation entièrement (convention `PUT` de l'API) : les champs
 * en lecture seule envoyés sont ignorés côté back, les autres remplacent la
 * valeur précédente — un budget non fourni n'est pas conservé.
 */
const putLimitation = async (
  serviceId: number,
  limitation: ApiLimitation
): Promise<ApiLimitation> => {
  const response = await axios.put<ApiLimitation>(
    `/api/services/telephonie/${serviceId}/limitation`,
    limitation
  );
  return response.data;
};

/** Ressource per-number authorization overrides (`GET`/`PUT .../number/{numberId}/authorization-overrides`). */
export interface ApiAuthorizationOverride {
  type: string;
  label: string;
  /** Indicatifs ou explications de la catégorie ; absent quand le legacy n'en portait aucun. */
  description?: string;
  override: 'inherit' | 'allow' | 'deny';
}

export interface ApiAuthorizationOverrides {
  serviceId: number;
  numberId: number;
  authorizations: ApiAuthorizationOverride[];
}

const getAuthorizationOverrides = async (
  serviceId: number,
  numberId: number
): Promise<ApiAuthorizationOverrides> => {
  const response = await axios.get<ApiAuthorizationOverrides>(
    `/api/services/telephonie/${serviceId}/number/${numberId}/authorization-overrides`
  );
  return response.data;
};

const putAuthorizationOverrides = async (
  serviceId: number,
  numberId: number,
  authorizationOverrides: ApiAuthorizationOverrides
): Promise<ApiAuthorizationOverrides> => {
  const response = await axios.put<ApiAuthorizationOverrides>(
    `/api/services/telephonie/${serviceId}/number/${numberId}/authorization-overrides`,
    authorizationOverrides
  );
  return response.data;
};

export type ApiIpZone = 'nowhere' | 'switzerland' | 'france' | 'europe' | 'world';

/** Ressource IP restriction, niveau service (`GET`/`PUT .../ip-restriction`). */
export interface ApiIpRestriction {
  serviceId: number;
  zone: ApiIpZone;
  ipExceptions: string[];
  msTeamsAllowed: boolean;
}

const getIpRestriction = async (serviceId: number): Promise<ApiIpRestriction> => {
  const response = await axios.get<ApiIpRestriction>(
    `/api/services/telephonie/${serviceId}/ip-restriction`
  );
  return response.data;
};

const putIpRestriction = async (
  serviceId: number,
  ipRestriction: ApiIpRestriction
): Promise<ApiIpRestriction> => {
  const response = await axios.put<ApiIpRestriction>(
    `/api/services/telephonie/${serviceId}/ip-restriction`,
    ipRestriction
  );
  return response.data;
};

/**
 * Ressource IP restriction, niveau numéro (`GET`/`PUT .../number/{numberId}/ip-restriction`) :
 * la lecture renvoie les valeurs effectives (l'override du numéro, ou celles
 * du service quand `inherited` vaut `true`) ; l'écriture est soit `{inherited:
 * true}` seul (retrait de l'override), soit `{zone, ipExceptions}` explicites.
 */
export interface ApiNumberIpRestriction {
  serviceId: number;
  numberId: number;
  zone: ApiIpZone;
  ipExceptions: string[];
  msTeamsAllowed: boolean;
  inherited: boolean;
}

export type ApiNumberIpRestrictionUpdate =
  | {inherited: true}
  | {zone: ApiIpZone; ipExceptions: string[]};

const getNumberIpRestriction = async (
  serviceId: number,
  numberId: number
): Promise<ApiNumberIpRestriction> => {
  const response = await axios.get<ApiNumberIpRestriction>(
    `/api/services/telephonie/${serviceId}/number/${numberId}/ip-restriction`
  );
  return response.data;
};

const putNumberIpRestriction = async (
  serviceId: number,
  numberId: number,
  update: ApiNumberIpRestrictionUpdate
): Promise<ApiNumberIpRestriction> => {
  const response = await axios.put<ApiNumberIpRestriction>(
    `/api/services/telephonie/${serviceId}/number/${numberId}/ip-restriction`,
    update
  );
  return response.data;
};

interface ApiConsommationLigne {
  libelle: string;
  dureeSecondes: number;
  montant: number;
  currency: string;
}

/** Réponse de l'agrégat back `services/telephonie/suivi-consommation` (toutes les prestations du client). */
interface ApiSuiviConsommation {
  consommations: ApiConsommationLigne[];
  dureeTotaleSecondes: number;
  montantTotal: number;
  plafond: number;
  pourcentage: number;
  currency: string;
}

/**
 * Suivi de consommation global : agrège toutes les prestations Téléphonie du
 * client (contrairement à `getTrunkSipConsommation`, scopé à un `serviceId`) —
 * le back fait le fan-out par prestation et l'agrégation. `data`/`messages`/la
 * note de présélection n'ont pas de source côté back (ligne voix fixe) et
 * restent figés.
 */
const getSuiviConsommationTelHD = async (
  period: string,
  classement: SuiviConsommationClassement
): Promise<SuiviConsommationTelHD> => {
  const response = await axios.get<ApiSuiviConsommation>(
    '/api/services/telephonie/suivi-consommation',
    {params: {period, classement}}
  );

  const {consommations, dureeTotaleSecondes, montantTotal, plafond, pourcentage, currency} =
    response.data;
  const devise = symboleDevise(currency);

  return {
    devise: currency,
    consommations: consommations.map((ligne) => ({
      destination: ligne.libelle,
      duree: formatDuree(ligne.dureeSecondes),
      montant: `${ligne.montant.toFixed(2)}${devise}`,
    })),
    total: {
      duree: formatDuree(dureeTotaleSecondes),
      montant: `${montantTotal.toFixed(2)}${devise}`,
    },
    synthese: {
      montant: `${montantTotal.toFixed(2)} ${devise}`,
      voix: formatDuree(dureeTotaleSecondes),
      data: '0 o',
      messages: '0',
      plafond: formatMontant(plafond, currency),
      pourcentage,
    },
    notePreselection: "Aucun appel pré-sélection n'a été passé durant ce mois",
  };
};

/**
 * `TrunkSipCallRecordDto` côté back (espace-client-v2) expose `amount`/`currency`
 * à plat, contrairement au passthrough `/reporting/{direction}` qui relaie la
 * forme imbriquée `{amount: {amount, currency}}` d'api-telephonie telle quelle.
 */
interface ApiTrunkSipCallRecord {
  id: number;
  callDate: string;
  caller: string;
  callee: string;
  direction: CallDirection;
  durationSeconds: number;
  amount: string;
  currency: string;
  destination: string;
  callerContactName?: string | null;
}

/** Réponse de l'agrégat back `services/telephonie/{serviceId}/consommation` (une seule prestation). */
interface ApiTrunkSipConsommation {
  calls: ApiTrunkSipCallRecord[];
  plafond: number;
  currency: string;
}

/**
 * `calls` vient de l'API (appels sortants + entrants facturés du mois demandé,
 * déjà fusionnés et enrichis du nom de contact de l'appelant par le back). Le
 * montant consommé et le pourcentage sont recalculés ici à partir de ces appels ;
 * `plafond` vient de la ressource Limitation de la prestation, relayé tel quel par
 * le back. Les KPI et les appels ACD restent des données de maquette : l'API n'a
 * pas ces agrégats.
 */
const getTrunkSipConsommation = async (
  serviceId: number,
  period: string
): Promise<TrunkSipConsommation> => {
  const response = await axios.get<ApiTrunkSipConsommation>(
    `/api/services/telephonie/${serviceId}/consommation`,
    {params: {period}}
  );
  const {calls: apiCalls, plafond, currency} = response.data;

  const calls: ConsoCall[] = apiCalls.map((record) => ({
    date: record.callDate,
    appele: record.callee,
    nomAppelant: record.callerContactName ?? '—',
    numAppelant: record.caller,
    destination: record.destination,
    duree: record.durationSeconds,
    type: record.direction === 'inbound' ? 'Entrant' : 'Sortant',
    cout: parseMontant(record.amount),
  }));

  const dureeTotaleSecondes = calls.reduce((sum, call) => sum + call.duree, 0);
  const coutTotal = calls.reduce((sum, call) => sum + call.cout, 0);

  return {
    devise: currency,
    kpis: [
      {label: 'Coût total du mois', value: '79,00 €', sub: 'HT'},
      {label: 'Durée totale', value: '09h 00mn', sub: '08s de voix'},
      {label: 'Appels sortants', value: '142', sub: 'vers 38 destinations'},
      {label: 'Appels entrants', value: '87', sub: 'reçus ce mois'},
    ],
    forfait: {
      dureeConsommee: formatDureeConsommee(dureeTotaleSecondes),
      montant: `${formatMontant(coutTotal, currency)} / ${formatMontant(plafond, currency)}`,
      pourcentage: plafond > 0 ? (coutTotal / plafond) * 100 : 0,
    },
    calls,
    acdCalls: [
      {
        dateHeure: '04/07/2026 10:40:14',
        appelant: '~ACDCallCenter',
        utilisateur: '33199000020',
        numAppele: '33639980012',
        duree: 140,
        montant: 0.3,
      },
      {
        dateHeure: '05/07/2026 10:04:18',
        appelant: '~ACDCallCenter',
        utilisateur: '33199000020',
        numAppele: '33639980012',
        duree: 41,
        montant: 0.09,
      },
      {
        dateHeure: '10/07/2026 19:22:47',
        appelant: '~ACDCallCenter',
        utilisateur: '33199000020',
        numAppele: '33639980012',
        duree: 46,
        montant: 0.1,
      },
      {
        dateHeure: '11/07/2026 23:52:33',
        appelant: '~ACDCallCenter',
        utilisateur: '33199000020',
        numAppele: '33639980012',
        duree: 28,
        montant: 0.06,
      },
      {
        dateHeure: '12/07/2026 00:12:56',
        appelant: '~ACDCallCenter',
        utilisateur: '33199000020',
        numAppele: '33639980012',
        duree: 482,
        montant: 1.04,
      },
    ],
  };
};

export type CallDirection = 'outbound' | 'inbound';

export interface CallsPage {
  calls: ConsoCall[];
  total: number;
}

/**
 * Page d'appels d'une direction donnée, chargée depuis l'API (contrairement à
 * `calls` dans `getTrunkSipConsommation`, qui charge tout le mois d'un coup
 * pour les agrégats KPI / récaps). Alimente exclusivement le tableau paginé
 * « Récap. des appels » de l'onglet Suivi de consommation, dont le titre et
 * la direction interrogée suivent le filtre Type (sortants/entrants).
 */
const getCallsPage = async (
  serviceId: number,
  period: string,
  direction: CallDirection,
  page: number,
  itemsPerPage: number,
  caller?: string,
  order?: 'asc' | 'desc'
): Promise<CallsPage> => {
  const response = await axios.get<ApiCallRecordsResponse>(
    `/api/services/telephonie/${serviceId}/reporting/${direction}`,
    {params: {period, page, itemsPerPage, caller, order}}
  );

  return {
    calls: response.data.data.map((record) =>
      toConsoCall(record, direction === 'inbound' ? 'Entrant' : 'Sortant')
    ),
    total: response.data.total,
  };
};

/**
 * « Restriction d'appels » (Voice/LimitationsOutgoing.aspx — nom trompeur, gère en réalité le
 * blacklisting d'appels entrants) : numéros appelants bloqués pour un ou tous les numéros de la
 * prestation.
 */
interface ApiBlockedCaller {
  id: number;
  serviceId: number;
  caller: string;
  callee: string;
  comment?: string;
}

const toEntreeBlacklistEntrant = (blockedCaller: ApiBlockedCaller): EntreeBlacklistEntrant => ({
  id: blockedCaller.id,
  numeroAppelant: blockedCaller.caller,
  numeroAppele: blockedCaller.callee,
});

const getBlacklistEntrants = async (serviceId: number): Promise<EntreeBlacklistEntrant[]> => {
  const response = await axios.get<ApiBlockedCaller[]>(
    `/api/services/telephonie/${serviceId}/blocked-caller`
  );
  return response.data.map(toEntreeBlacklistEntrant);
};

/** Un appel par numéro appelant : il n'existe pas de route de blocage groupé. */
const ajouterBlacklistEntrant = async (
  serviceId: number,
  numeroAppelant: string,
  numeroAppele: string
): Promise<EntreeBlacklistEntrant> => {
  const response = await axios.post<ApiBlockedCaller>(
    `/api/services/telephonie/${serviceId}/blocked-caller`,
    {caller: numeroAppelant, callee: numeroAppele}
  );
  return toEntreeBlacklistEntrant(response.data);
};

const supprimerBlacklistEntrant = async (serviceId: number, id: number): Promise<void> => {
  await axios.delete(`/api/services/telephonie/${serviceId}/blocked-caller/${id}`);
};

/**
 * Numéro que la prestation est autorisée à présenter comme identité appelante
 * sans l'héberger (`VirtualNumber` d'api-telephonie, reprise de l'écran G2000
 * « Ajouter Numéro Virtuel ») — à proposer au même titre que les numéros du
 * trunk dans le choix du numéro affiché en appels sortants.
 */
interface ApiVirtualNumber {
  id: number;
  number: string;
}

const getVirtualNumbers = async (serviceId: number): Promise<string[]> => {
  const response = await axios.get<ApiVirtualNumber[]>(
    `/api/services/telephonie/${serviceId}/virtualnumber`
  );
  return response.data.map((virtualNumber) => virtualNumber.number);
};

/**
 * Voice.asmx/GetTicket (obj.Show="incoming") — tickets d'appels entrants de la période/numéro
 * choisis. `numeroAppele` omis pour « tous les numéros » : le filtre `callee` ne s'applique
 * qu'à un numéro précis.
 */
const getTicketsAppelsEntrants = async (
  serviceId: number,
  periode: string,
  numeroAppele?: string
): Promise<TicketAppelEntrant[]> => {
  const response = await axios.get<ApiCallRecordsResponse>(
    `/api/services/telephonie/${serviceId}/reporting/inbound`,
    {params: {period: periode, callee: numeroAppele}}
  );
  return response.data.data.map((record) => ({
    recno: record.id,
    numeroAppelant: record.caller,
    dateHeure: record.callDate,
  }));
};

/** Voice/LimitationAntiSpam.aspx — activation globale/par numéro + exceptions individuelles. */
const getConfigurationAntiSpamAppel = async (): Promise<ConfigurationAntiSpamAppel> => {
  return Promise.resolve({
    tousLesNumeros: true,
    actif: true,
    exceptions: [
      {idWhitelist: 1, numero: '0710000001', numeroFormate: '071 000 00 01', actif: true},
      {idWhitelist: 2, numero: '0710000002', numeroFormate: '071 000 00 02', actif: false},
    ],
  });
};

export interface Annuaire {
  contacts: AnnuaireContact[];
}

const getAnnuaire = async (): Promise<Annuaire> => {
  return Promise.resolve({
    contacts: [
      {id: 1, nom: 'TEST GLOBAL', numero: '+33102030405', raccourci: '6078'},
      {id: 2, nom: 'iDC6', numero: '+33637237701', raccourci: ''},
    ],
  });
};

export interface AnnuaireInterne {
  entrees: AnnuaireInterneEntry[];
}

export interface PlagesHoraires {
  groupes: PlageHoraireGroupe[];
}

const getPlagesHoraires = async (): Promise<PlagesHoraires> => {
  return Promise.resolve({
    groupes: [
      {
        id: 1,
        nom: 'Ouverture des bureaux',
        type: 'ouverture',
        plages: [
          {
            id: 1,
            nom: 'Ouverture des bureaux',
            depuis: '2019-09-10T11:00:00',
            periodicite: 'semaine',
            jours: ['lun', 'mar', 'mer', 'jeu', 'ven'],
            periodes: [
              {heureDebut: '08:00', heureFin: '12:00'},
              {heureDebut: '14:00', heureFin: '18:00'},
            ],
          },
        ],
      },
      {
        id: 2,
        nom: 'ouverture Genève',
        type: 'ouverture',
        plages: [
          {
            id: 1,
            nom: 'Ouverture Genève semaine',
            depuis: '2020-01-15T09:00:00',
            periodicite: 'semaine',
            jours: ['lun', 'mar', 'mer', 'jeu', 'ven'],
            periodes: [{heureDebut: '08:30', heureFin: '17:30'}],
          },
          {
            id: 2,
            nom: 'Ouverture Genève samedi',
            depuis: '2020-01-15T09:00:00',
            periodicite: 'semaine',
            jours: ['sam'],
            periodes: [{heureDebut: '09:00', heureFin: '12:00'}],
          },
        ],
      },
      {
        id: 3,
        nom: 'Weekly opening Zürich',
        type: 'ouverture',
        plages: [
          {
            id: 1,
            nom: 'Weekly opening Zürich – Lundi',
            depuis: '2021-03-01T10:00:00',
            periodicite: 'semaine',
            jours: ['lun'],
            periodes: [{heureDebut: '08:00', heureFin: '17:00'}],
          },
          {
            id: 2,
            nom: 'Weekly opening Zürich – Mardi',
            depuis: '2021-03-01T10:00:00',
            periodicite: 'semaine',
            jours: ['mar'],
            periodes: [{heureDebut: '08:00', heureFin: '17:00'}],
          },
          {
            id: 3,
            nom: 'Weekly opening Zürich – Mer.-Ven.',
            depuis: '2021-03-01T10:00:00',
            periodicite: 'semaine',
            jours: ['mer', 'jeu', 'ven'],
            periodes: [{heureDebut: '08:00', heureFin: '16:00'}],
          },
        ],
      },
      {
        id: 4,
        nom: 'Ferien Zürich',
        type: 'vacances',
        plages: [
          {
            id: 1,
            nom: 'Ferien Zürich',
            depuis: '2026-01-01T00:00:00',
            finLe: '2026-01-01T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
          {
            id: 2,
            nom: 'Ferien Zürich',
            depuis: '2026-01-02T00:00:00',
            finLe: '2026-01-02T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
          {
            id: 3,
            nom: 'Ferien Zürich',
            depuis: '2026-04-03T00:00:00',
            finLe: '2026-04-06T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
          {
            id: 4,
            nom: 'Ferien Zürich',
            depuis: '2026-05-01T00:00:00',
            finLe: '2026-05-01T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
          {
            id: 5,
            nom: 'Ferien Zürich',
            depuis: '2026-05-14T00:00:00',
            finLe: '2026-05-14T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
          {
            id: 6,
            nom: 'Ferien Zürich',
            depuis: '2026-05-25T00:00:00',
            finLe: '2026-05-25T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
          {
            id: 7,
            nom: 'Ferien Zürich',
            depuis: '2026-08-01T00:00:00',
            finLe: '2026-08-01T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
          {
            id: 8,
            nom: 'Ferien Zürich',
            depuis: '2026-12-25T00:00:00',
            finLe: '2026-12-26T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
          {
            id: 9,
            nom: 'Ferien Zürich',
            depuis: '2026-12-31T00:00:00',
            finLe: '2026-12-31T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
        ],
      },
      {
        id: 5,
        nom: 'Gr Holiday Ael',
        type: 'vacances',
        plages: [
          {
            id: 1,
            nom: 'Gr Holiday Ael',
            depuis: '2026-07-06T00:00:00',
            finLe: '2026-07-24T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
        ],
      },
      {
        id: 6,
        nom: 'Jours fériés BE',
        type: 'vacances',
        plages: [
          {
            id: 1,
            nom: 'Jours fériés BE',
            depuis: '2026-01-01T00:00:00',
            finLe: '2026-01-01T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
          {
            id: 2,
            nom: 'Jours fériés BE',
            depuis: '2026-01-02T00:00:00',
            finLe: '2026-01-02T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
          {
            id: 3,
            nom: 'Jours fériés BE',
            depuis: '2026-04-06T00:00:00',
            finLe: '2026-04-06T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
          {
            id: 4,
            nom: 'Jours fériés BE',
            depuis: '2026-05-01T00:00:00',
            finLe: '2026-05-01T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
          {
            id: 5,
            nom: 'Jours fériés BE',
            depuis: '2026-12-25T00:00:00',
            finLe: '2026-12-25T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
        ],
      },
      {
        id: 7,
        nom: 'vacances GE',
        type: 'vacances',
        plages: [
          {
            id: 1,
            nom: 'vacances GE',
            depuis: '2026-08-01T00:00:00',
            finLe: '2026-08-15T23:59:00',
            periodicite: 'aucune',
            periodes: [{heureDebut: '00:00', heureFin: '23:59'}],
          },
        ],
      },
      {
        id: 8,
        nom: 'Ouverture support',
        type: 'ouverture',
        plages: [
          {
            id: 1,
            nom: 'Ouverture support',
            depuis: '2020-01-01T00:00:00',
            periodicite: 'semaine',
            jours: ['lun', 'mar', 'mer', 'jeu', 'ven'],
            periodes: [{heureDebut: '07:30', heureFin: '19:00'}],
          },
        ],
      },
    ],
  });
};

export interface PbxGroupes {
  groupes: PbxGroupeAppel[];
}

const CONDITION_VIDE_GROUPE = {message: '', devierVers: 'fin_appel'};

const getPbxGroupes = async (): Promise<PbxGroupes> => {
  return Promise.resolve({
    groupes: [
      {
        id: 1,
        nom: 'Groupe Commercial',
        numeroCourt: '200',
        typeDistribution: 'cyclique',
        dureeSonnerieParAgent: '15s',
        membres: ['021 566 10 01', '022 566 79 34'],
        messageAccueil: '',
        messageAttente: '',
        messageAvantReponse: '',
        musiqueAttente: '',
        nbMaxAppelantsFile: '5',
        condNbMaxAppelants: CONDITION_VIDE_GROUPE,
        dureeAttenteMax: '1min',
        condDureeAttenteMax: CONDITION_VIDE_GROUPE,
        dureeSonnerieMax: '15s',
        condDureeSonnerieMax: CONDITION_VIDE_GROUPE,
        gestionAppelsRepondus: 'inactif',
        desactivationMessage: '',
        desactivationDevierVers: 'fin_appel',
        estAcd: true,
        alias: 'GrCommercial',
        actif: true,
      },
      {
        id: 2,
        nom: 'Groupe Support',
        numeroCourt: '201',
        typeDistribution: 'hierarchique',
        dureeSonnerieParAgent: '20s',
        membres: ['022 566 79 37'],
        messageAccueil: '',
        messageAttente: '',
        messageAvantReponse: '',
        musiqueAttente: '',
        nbMaxAppelantsFile: '5',
        condNbMaxAppelants: CONDITION_VIDE_GROUPE,
        dureeAttenteMax: '1min',
        condDureeAttenteMax: CONDITION_VIDE_GROUPE,
        dureeSonnerieMax: '20s',
        condDureeSonnerieMax: CONDITION_VIDE_GROUPE,
        gestionAppelsRepondus: 'inactif',
        desactivationMessage: '',
        desactivationDevierVers: 'fin_appel',
        estAcd: true,
        alias: 'GrSupport',
        actif: false,
      },
      {
        id: 3,
        nom: 'Groupe Historique',
        numeroCourt: '202',
        typeDistribution: 'aleatoire',
        dureeSonnerieParAgent: '25s',
        membres: [],
        messageAccueil: '',
        messageAttente: '',
        messageAvantReponse: '',
        musiqueAttente: '',
        nbMaxAppelantsFile: '5',
        condNbMaxAppelants: CONDITION_VIDE_GROUPE,
        dureeAttenteMax: '1min',
        condDureeAttenteMax: CONDITION_VIDE_GROUPE,
        dureeSonnerieMax: '25s',
        condDureeSonnerieMax: CONDITION_VIDE_GROUPE,
        gestionAppelsRepondus: 'inactif',
        desactivationMessage: '',
        desactivationDevierVers: 'fin_appel',
        estAcd: false,
        actif: false,
      },
    ],
  });
};

const getAnnuaireInterne = async (): Promise<AnnuaireInterne> => {
  return Promise.resolve({
    entrees: [
      {nom: 'Mickael_BERTHE', numero: '4001'},
      {nom: 'Jenny_MARIE', numero: '4208'},
      {nom: 'Cindy_SOPHIE', numero: '4210'},
      {nom: 'Julien_HARBERS', numero: '4300'},
      {nom: 'Bertrand_PIERRE_LOUIS', numero: '4315'},
      {nom: 'Frederic_JEAN_PIERRE', numero: '4336'},
      {nom: 'Jenita_RAMASAMY', numero: '4349'},
      {nom: 'Ubaid_NUJJOO', numero: '4413'},
      {nom: 'Benjamin_RENAULT', numero: '4424'},
      {nom: 'Tarik_AMINE', numero: '4425'},
      {nom: 'Sophie_MARTIN', numero: '4430'},
      {nom: 'Laurent_DUBOIS', numero: '4442'},
      {nom: 'Camille_LEROY', numero: '4458'},
      {nom: 'Nicolas_GARCIA', numero: '4467'},
      {nom: 'Amelie_ROUX', numero: '4489'},
      {nom: 'Thomas_FONTAINE', numero: '4501'},
    ],
  });
};

const getIvrs = async (): Promise<Ivr[]> => {
  return Promise.resolve([
    {
      id: 1,
      nom: 'Accueil',
      numeroCourt: '200',
      annonceAccueil: 'Bienvenue',
      actions: [
        {touche: '1', type: 'groupe', cible: 'Groupe Commercial'},
        {touche: '2', type: 'numero', cible: '+41791234567'},
        {touche: '3', type: 'annonce', cible: 'Fermeture annuelle'},
        {touche: '9', type: 'rejouer_menu'},
        {touche: '0', type: 'fin_appel'},
      ],
    },
    {
      id: 2,
      nom: 'Support',
      numeroCourt: '201',
      actions: [{touche: '1', type: 'groupe', cible: 'Groupe Support'}],
    },
  ]);
};

export interface VirtualPbxNumeros {
  numerosGlobaux: NumeroGlobal[];
}

const getVirtualPbxNumeros = async (): Promise<VirtualPbxNumeros> => {
  return Promise.resolve({
    numerosGlobaux: [
      {
        id: 1,
        numero: '0215687805',
        numeroFormate: '021 568 78 05',
        nom: 'Ligne principale',
        rejetAppelsAnonymes: false,
        plages: [
          {
            idPlage: 1,
            nomPlage: 'Ouverture des bureaux',
            typeGroupe: 'distribution',
            nomGroupe: 'Groupe Commercial',
          },
          {
            idPlage: ID_PLAGE_VACANCES,
            nomPlage: 'Vacances',
            typeGroupe: 'menu',
            nomGroupe: 'IVR Vacances',
          },
          {
            idPlage: ID_PLAGE_HORS_PLAGE,
            nomPlage: 'Hors plage',
            typeGroupe: 'systeme',
            nomGroupe: 'VM Messagerie générale',
          },
        ],
      },
      {
        id: 2,
        numero: '0225667934',
        numeroFormate: '022 566 79 34',
        nom: 'Ligne support',
        rejetAppelsAnonymes: true,
        plages: [
          {
            idPlage: 2,
            nomPlage: 'Ouverture support',
            typeGroupe: 'distribution',
            nomGroupe: 'Groupe Support',
            alias: 'Support technique',
          },
          {
            idPlage: ID_PLAGE_VACANCES,
            nomPlage: 'Vacances',
            typeGroupe: 'menu',
            nomGroupe: 'IVR Vacances',
          },
          {
            idPlage: ID_PLAGE_HORS_PLAGE,
            nomPlage: 'Hors plage',
            typeGroupe: 'systeme',
            nomGroupe: 'VM Messagerie générale',
          },
        ],
      },
    ],
  });
};

export interface VirtualPbxActivite {
  prestationId: string;
  titre: string;
  lignes: PbxActiviteLigne[];
  numeroGlobal: string;
  autresNumerosAttribues: string[];
  teamsAssocie?: {prestationId: string; domaine: string};
}

const getVirtualPbxActivite = async (): Promise<VirtualPbxActivite> => {
  return Promise.resolve({
    prestationId: '600582',
    titre: 'VPBX avec teams, test FGU/AEL',
    lignes: [
      {
        utilisateur: 'Jean Dupont',
        telephone: '021 566 10 01',
        groupe: 'Groupe Commercial',
        connecte: true,
        disponible: true,
      },
      {
        utilisateur: 'Marie Lambert',
        telephone: '022 566 79 34',
        groupe: 'Groupe Commercial',
        connecte: true,
        disponible: true,
      },
      {
        utilisateur: 'Non attribué',
        telephone: '022 566 79 37',
        groupe: '',
        connecte: false,
        disponible: true,
      },
      {
        utilisateur: 'Non attribué',
        telephone: '022 566 79 38',
        groupe: '',
        connecte: true,
        disponible: false,
      },
      {
        utilisateur: 'Non attribué',
        telephone: '024 566 70 07',
        groupe: '',
        connecte: false,
        disponible: false,
      },
    ],
    numeroGlobal: '021 568 78 05',
    autresNumerosAttribues: ['022 566 79 34', '022 566 79 37', '024 566 70 07'],
    teamsAssocie: {prestationId: '624997', domaine: '624997.teams.lab.ipvoip.ch'},
  });
};

export interface LocalisationsUrgence {
  sites: LocalisationSite[];
}

interface ApiTelephonieLocation {
  id: number;
  serviceId: number;
  isDefault: boolean;
  label: string;
  customerName: string;
  street?: string | null;
  houseNumber?: string | null;
  zip?: string | null;
  city?: string | null;
  country?: string | null;
  municipalityId?: number | null;
  egid?: string | null;
  inseeCode?: string | null;
  gpsPosition?: {latitude: number; longitude: number} | null;
}

/** Le formulaire affiche le nom du pays, l'API un code ISO 3166-1 alpha-2. */
const PAYS_PAR_CODE: Record<string, string> = {FR: 'France', CH: 'Suisse', BE: 'Belgique'};
const CODE_PAR_PAYS: Record<string, string> = {France: 'FR', Suisse: 'CH', Belgique: 'BE'};

/**
 * municipalityId/egid/inseeCode/gpsPosition sont résolus via l'autocomplete
 * d'adresse EGLS (voir `searchAddresses`/`getSwissBuildingReference`) et
 * transportés depuis la sélection d'une suggestion ; envoyés tels quels ici.
 * S'ils sont absents (adresse belge, ou saisie manuelle sans sélection),
 * la plateforme voix legacy peut rejeter l'ajout/la modification (422,
 * codes -17 à -20).
 */
const siteVersApi = (site: Omit<LocalisationSite, 'id'>) => ({
  label: site.nom,
  customerName: site.client,
  isDefault: site.defaut,
  street: site.rue,
  houseNumber: site.numero,
  zip: site.cp,
  city: site.ville,
  country: CODE_PAR_PAYS[site.pays] ?? site.pays,
  municipalityId: site.municipalityId,
  egid: site.egid,
  inseeCode: site.inseeCode,
  gpsPosition:
    site.latitude !== undefined && site.longitude !== undefined
      ? {latitude: site.latitude, longitude: site.longitude}
      : undefined,
});

const siteDepuisApi = (location: ApiTelephonieLocation): LocalisationSite => ({
  id: location.id,
  defaut: location.isDefault,
  nom: location.label,
  client: location.customerName,
  cp: location.zip ?? '',
  ville: location.city ?? '',
  rue: location.street ?? '',
  numero: location.houseNumber ?? '',
  pays: (location.country && PAYS_PAR_CODE[location.country]) ?? location.country ?? '',
  municipalityId: location.municipalityId ?? undefined,
  egid: location.egid ?? undefined,
  inseeCode: location.inseeCode ?? undefined,
  latitude: location.gpsPosition?.latitude,
  longitude: location.gpsPosition?.longitude,
});

export interface AddressSuggestion {
  id: string;
  featureId?: string;
  label: string;
  street?: string;
  houseNumber?: string;
  zip?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  inseeCode?: string;
}

interface ApiSwissBuildingReference {
  municipalityId: number | null;
  egid: string | null;
  latitude: number | null;
  longitude: number | null;
}

/**
 * Suggestions d'adresse issues du référentiel EGLS (VTX), utilisées par
 * l'autocomplete du formulaire de localisation d'urgence. Ne couvre que FR
 * (code INSEE direct) et CH (nécessite ensuite `getSwissBuildingReference`
 * pour municipalityId/egid, absents de cette réponse).
 */
const searchAddresses = async (
  q: string,
  country: 'FR' | 'CH',
  zipcode?: string,
  city?: string
): Promise<AddressSuggestion[]> => {
  const response = await axios.get<AddressSuggestion[]>('/api/services/telephonie/address-search', {
    params: {q, country, zipcode, city},
  });

  return response.data;
};

/** Résout municipalityId/egid (+coordonnées) pour une suggestion suisse déjà sélectionnée. */
const getSwissBuildingReference = async (
  featureId: string
): Promise<ApiSwissBuildingReference | null> => {
  const response = await axios.get<ApiSwissBuildingReference | null>(
    '/api/services/telephonie/address-geo-site',
    {params: {featureId}}
  );

  return response.data;
};

/**
 * Sans serviceId, retourne la maquette. Avec un serviceId, appelle l'API réelle
 * (api-telephonie) — même convention que `getTrunkNumeros`.
 */
const getLocalisationsUrgence = async (serviceId?: number): Promise<LocalisationsUrgence> => {
  if (serviceId === undefined) {
    return Promise.resolve({
      sites: [
        {
          id: 1,
          defaut: true,
          nom: 'Pully',
          client: 'Developpement CELESTE',
          cp: '1009',
          ville: 'Pully',
          rue: 'Avenue de Lavaux',
          numero: '101',
          pays: 'Suisse',
        },
      ],
    });
  }

  const locations = await fetchLocations(serviceId);

  return {sites: locations.map(siteDepuisApi)};
};

const fetchLocations = async (serviceId: number): Promise<ApiTelephonieLocation[]> => {
  const response = await axios.get<ApiTelephonieLocation[]>(
    `/api/services/telephonie/${serviceId}/location`
  );

  return response.data;
};

/**
 * `ApiTelephonieNumber.location`/`ApiNumberDetail.location` ne portent qu'un
 * `label` (nom du site) — l'adresse complète (rue, CP, ville) n'existe que côté
 * ressource Location (`GET .../location`, voir `fetchLocations`). Sans
 * correspondance dans `locationsParId` (site supprimé entre-temps, par ex.),
 * on retombe sur le seul label, comme avant.
 */
const siteLignesDepuisLocation = (
  label: string | null | undefined,
  locationId: number | null | undefined,
  locationsParId: Map<number, ApiTelephonieLocation>
): string[] => {
  const location =
    locationId !== null && locationId !== undefined ? locationsParId.get(locationId) : undefined;
  if (!location) {
    return label ? [label] : [];
  }

  const rue = `${location.street ?? ''} ${location.houseNumber ?? ''}`.trim();
  const ville = `${location.zip ?? ''} ${location.city ?? ''}`.trim();

  return [location.label, rue, ville].filter((ligne) => ligne !== '');
};

const ajouterLocalisationUrgence = async (
  serviceId: number,
  site: Omit<LocalisationSite, 'id'>
): Promise<LocalisationSite> => {
  const response = await axios.post<ApiTelephonieLocation>(
    `/api/services/telephonie/${serviceId}/location`,
    siteVersApi(site)
  );

  return siteDepuisApi(response.data);
};

const modifierLocalisationUrgence = async (
  serviceId: number,
  id: number,
  site: Omit<LocalisationSite, 'id'>
): Promise<LocalisationSite> => {
  const response = await axios.put<ApiTelephonieLocation>(
    `/api/services/telephonie/${serviceId}/location/${id}`,
    siteVersApi(site)
  );

  return siteDepuisApi(response.data);
};

const supprimerLocalisationUrgence = async (serviceId: number, id: number): Promise<void> => {
  await axios.delete(`/api/services/telephonie/${serviceId}/location/${id}`);
};

export interface TrunkNumeros {
  numeros: TrunkNumero[];
  total: number;
  prestation: string;
}

const boiteVocaleParDefaut = (): InfoRow[] => [
  {label: 'Statut', value: 'Désactivée'},
  {label: 'Langue', value: 'Français'},
  {label: 'Adresse E-Mail', value: 'Définir un E-mail', link: true},
  {label: 'Mot de passe', value: ''},
  {label: 'Notification par SMS', value: 'Non'},
];

const appelsEntrantsParDefaut = (): InfoRow[] => [
  {label: "Redirection en cas d'absence", value: 'Non', danger: true},
  {label: 'Redirection si occupé', value: 'Non', danger: true},
  {label: 'Redirection inconditionnelle', value: 'Non', danger: true},
  {label: 'Mise en attente', value: 'Non', danger: true},
  {label: 'Rejet des appels anonymes', value: 'Non', danger: true},
  {label: "Protection contre les renvois d'appels", value: 'Non', danger: true},
  {label: "Signal d'appel sur ligne directe", value: 'Non', danger: true},
  {label: 'Sonnerie en parallèle', value: 'Non', danger: true},
];

const limitationsParDefaut = (): InfoRow[] => [
  {label: 'Appels sortants', value: 'Aucune'},
  {label: 'Appels entrants', value: 'Aucune'},
  {label: 'Filtrage IP', value: 'Non', danger: true},
];

/**
 * `numberType` (pas `connectRole`, jamais renvoyé par cet endpoint — voir
 * ADR-0010 côté api-telephonie) est l'unique signal réel pour distinguer le
 * numéro principal : `CentrexMainNumber`/`TrunkingMainNumber` selon le type
 * de service.
 */
interface ApiTelephonieNumber {
  numberId: number;
  number: string;
  active: boolean;
  numberType:
    | 'CentrexMainNumber'
    | 'TrunkingMainNumber'
    | 'TrunkingLinkedNumber'
    | 'Conference'
    | 'Reserved'
    | 'Classic'
    | null;
  sipUsername: string | null;
  server: string | null;
  proxy: string | null;
  contact?: {firstname: string | null; lastname: string | null} | null;
  location?: {locationId: number | null; label: string | null} | null;
}

const estPrincipal = (numberType: string | null): boolean =>
  numberType === 'CentrexMainNumber' || numberType === 'TrunkingMainNumber';

const trunkNumeroDepuisApi = (
  n: ApiTelephonieNumber,
  locationsParId: Map<number, ApiTelephonieLocation>
): TrunkNumero => ({
  numeroId: n.numberId,
  numero: n.number,
  attribueA: n.contact ? `${n.contact.firstname ?? ''} ${n.contact.lastname ?? ''}`.trim() : '',
  site: siteLignesDepuisLocation(n.location?.label, n.location?.locationId, locationsParId),
  locationId: n.location?.locationId ?? null,
  principal: estPrincipal(n.numberType),
  actif: n.active,
  lie: n.numberType === 'TrunkingLinkedNumber',
  reserve: n.numberType === 'Reserved',
  identifiantsSip: {
    nomUtilisateur: n.sipUsername ?? '',
    motDePasse: '',
    serveurSip: n.server ?? '',
    proxySip: n.proxy ?? '',
    npa: '',
    port: '',
  },
  boiteVocale: boiteVocaleParDefaut(),
  appelsEntrants: appelsEntrantsParDefaut(),
  limitations: limitationsParDefaut(),
});

const SITE_EXEMPLE = ['Site exemple', "1 avenue de l'Exemple", '75001 Paris'];

const identifiantsSipPour = (numero: string, motDePasse: string): IdentifiantsSip => ({
  nomUtilisateur: `417${numero.replace(/\s/g, '')}lbo`,
  motDePasse,
  serveurSip: 's1.112056.trk.ipvoip.ch',
  proxySip: 's1.112056.trk.ipvoip.ch',
  npa: '9403',
  port: '5060',
});

/**
 * Sans serviceId (utilisation en simple source de numéros pour un filtre, dans des
 * écrans non encore branchés : SVI, restriction d'appels, localisations d'urgence),
 * retourne la maquette. Avec un serviceId (écran « Gérer vos numéros »), appelle
 * l'API réelle — le contact, le site (`location`), le mot de passe SIP et les
 * réglages boîte vocale / appels entrants / limitations ne sont pas (encore) portés
 * par l'API et gardent leurs valeurs par défaut.
 */
const getTrunkNumeros = async (serviceId?: number): Promise<TrunkNumeros> => {
  if (serviceId === undefined) {
    return Promise.resolve({
      numeros: [
        {
          numeroId: 1,
          numero: '071 000 00 01',
          attribueA: 'Jean DUPONT',
          site: SITE_EXEMPLE,
          principal: true,
          actif: true,
          lie: false,
          reserve: false,
          identifiantsSip: identifiantsSipPour('071 000 00 01', 'In83cl'),
          boiteVocale: [
            {label: 'Statut', value: 'Activée'},
            {label: 'Langue', value: 'Français'},
            {label: 'Adresse E-Mail', value: 'jean.dupont@exemple.fr'},
            {label: 'Mot de passe', value: 'Voir', link: true},
            {label: 'Notification par SMS', value: 'Oui'},
          ],
          appelsEntrants: [
            {label: "Redirection en cas d'absence", value: 'Boîte vocale'},
            {label: 'Redirection si occupé', value: 'Non', danger: true},
            {label: 'Redirection inconditionnelle', value: 'Non', danger: true},
            {label: 'Mise en attente', value: 'Non', danger: true},
            {label: 'Rejet des appels anonymes', value: 'Non', danger: true},
            {label: "Protection contre les renvois d'appels", value: 'Non', danger: true},
            {label: "Signal d'appel sur ligne directe", value: 'Non', danger: true},
            {label: 'Sonnerie en parallèle', value: 'Non', danger: true},
          ],
          limitations: limitationsParDefaut(),
        },
        {
          numeroId: 2,
          numero: '071 000 00 02',
          attribueA: '',
          site: SITE_EXEMPLE,
          principal: false,
          actif: true,
          lie: false,
          reserve: false,
          identifiantsSip: identifiantsSipPour('071 000 00 02', 'Zk92pq'),
          boiteVocale: boiteVocaleParDefaut(),
          appelsEntrants: appelsEntrantsParDefaut(),
          limitations: limitationsParDefaut(),
        },
        {
          numeroId: 3,
          numero: '071 000 00 10',
          attribueA: '',
          site: SITE_EXEMPLE,
          principal: false,
          actif: true,
          lie: false,
          reserve: false,
          identifiantsSip: identifiantsSipPour('071 000 00 10', 'Bt74hm'),
          boiteVocale: boiteVocaleParDefaut(),
          appelsEntrants: appelsEntrantsParDefaut(),
          limitations: limitationsParDefaut(),
        },
        {
          numeroId: 4,
          numero: '071 000 00 11',
          attribueA: '',
          site: SITE_EXEMPLE,
          principal: false,
          actif: false,
          lie: false,
          reserve: false,
          identifiantsSip: identifiantsSipPour('071 000 00 11', 'Qw38rt'),
          boiteVocale: boiteVocaleParDefaut(),
          appelsEntrants: appelsEntrantsParDefaut(),
          limitations: limitationsParDefaut(),
        },
        {
          numeroId: 5,
          numero: '071 000 00 12',
          attribueA: '',
          site: SITE_EXEMPLE,
          principal: false,
          actif: true,
          lie: false,
          reserve: false,
          identifiantsSip: identifiantsSipPour('071 000 00 12', 'Lp56vn'),
          boiteVocale: boiteVocaleParDefaut(),
          appelsEntrants: appelsEntrantsParDefaut(),
          limitations: limitationsParDefaut(),
        },
        {
          numeroId: 6,
          numero: '071 000 00 13',
          attribueA: '',
          site: SITE_EXEMPLE,
          principal: false,
          actif: true,
          lie: false,
          reserve: false,
          identifiantsSip: identifiantsSipPour('071 000 00 13', 'Xc19gh'),
          boiteVocale: boiteVocaleParDefaut(),
          appelsEntrants: appelsEntrantsParDefaut(),
          limitations: limitationsParDefaut(),
        },
        {
          numeroId: 7,
          numero: '071 000 00 14',
          attribueA: '',
          site: SITE_EXEMPLE,
          principal: false,
          actif: false,
          lie: false,
          reserve: false,
          identifiantsSip: identifiantsSipPour('071 000 00 14', 'Mn83jk'),
          boiteVocale: boiteVocaleParDefaut(),
          appelsEntrants: appelsEntrantsParDefaut(),
          limitations: limitationsParDefaut(),
        },
        {
          numeroId: 8,
          numero: '071 000 00 15',
          attribueA: '',
          site: SITE_EXEMPLE,
          principal: false,
          actif: true,
          lie: false,
          reserve: false,
          identifiantsSip: identifiantsSipPour('071 000 00 15', 'Rt45yu'),
          boiteVocale: boiteVocaleParDefaut(),
          appelsEntrants: appelsEntrantsParDefaut(),
          limitations: limitationsParDefaut(),
        },
        {
          numeroId: 9,
          numero: '071 000 00 16',
          attribueA: '',
          site: SITE_EXEMPLE,
          principal: false,
          actif: true,
          lie: false,
          reserve: false,
          identifiantsSip: identifiantsSipPour('071 000 00 16', 'Fg67bn'),
          boiteVocale: boiteVocaleParDefaut(),
          appelsEntrants: appelsEntrantsParDefaut(),
          limitations: limitationsParDefaut(),
        },
      ],
      total: 31,
      prestation: 'Connect PBX-IP 8 — Abonnement (308073)',
    });
  }

  const [response, locations] = await Promise.all([
    axios.get<ApiTelephonieNumber[]>(`/api/services/telephonie/${serviceId}/number`),
    fetchLocations(serviceId),
  ]);
  const locationsParId = new Map(locations.map((location) => [location.id, location]));

  const numeros: TrunkNumero[] = response.data.map((n) => trunkNumeroDepuisApi(n, locationsParId));

  return {
    numeros,
    total: numeros.length,
    // Pas encore de libellé de prestation côté api-telephonie.
    prestation: '',
  };
};

const redirectEnLigne = (label: string, redirect: CallRedirectInfo): InfoRow =>
  redirect.enabled
    ? {label, value: redirect.target === 'voicemail' ? 'Boîte vocale' : redirect.target}
    : {label, value: 'Non', danger: true};

interface ApiNumberDetail {
  number: string;
  numberType: ApiTelephonieNumber['numberType'];
  active: boolean;
  tariffClass: string | null;
  contact?: {contactManagerName: string | null} | null;
  location?: {locationId: number | null; label: string | null} | null;
  sipAccount?: {username: string | null; sipServer: string | null; sipProxy: string | null} | null;
  softphone?: {username: string | null} | null;
  voicemail?: {
    enabled: boolean | null;
    announcementOnly: boolean | null;
    keepOnServer: boolean | null;
    email: string | null;
    language: string | null;
    password: string | null;
  } | null;
  inboundCalls?: {
    unavailableRedirect: CallRedirectInfo;
    noAnswerTimeout: number | null;
    busyRedirect: CallRedirectInfo;
    unconditionalRedirect: CallRedirectInfo;
    backupNumber: string | null;
    backupEnabled?: boolean | null;
    parallelRingingEnabled: boolean | null;
    parallelRingingNumber: string | null;
    parallelRingingAllowed: boolean | null;
    rejectAnonymousCalls: boolean | null;
    rejectForwardedCalls: boolean | null;
    callWaiting: boolean | null;
    musicOnHold: boolean | null;
  } | null;
  outboundCalls?: {callerIdHidden: boolean | null; callerIdNumber: string | null} | null;
}

const trunkNumeroDepuisDetailApi = (
  n: ApiNumberDetail,
  locationsParId: Map<number, ApiTelephonieLocation>
): TrunkNumero => {
  const detail: NumeroDetail = {
    tariffClass: n.tariffClass ?? null,
    location: n.location ?? null,
    sipAccount: n.sipAccount ?? null,
    softphone: n.softphone ?? null,
    voicemail: n.voicemail ?? null,
    inboundCalls: n.inboundCalls ?? null,
    outboundCalls: n.outboundCalls ?? null,
  };

  return {
    numeroId: 0, // écrasé juste après par l'appelant, qui connaît déjà le vrai id.
    numero: n.number,
    attribueA: n.contact?.contactManagerName ?? '',
    site: siteLignesDepuisLocation(n.location?.label, n.location?.locationId, locationsParId),
    locationId: n.location?.locationId ?? null,
    principal: estPrincipal(n.numberType),
    actif: n.active,
    lie: n.numberType === 'TrunkingLinkedNumber',
    reserve: n.numberType === 'Reserved',
    identifiantsSip: {
      nomUtilisateur: detail.sipAccount?.username ?? '',
      motDePasse: '',
      serveurSip: detail.sipAccount?.sipServer ?? '',
      proxySip: detail.sipAccount?.sipProxy ?? '',
      npa: '',
      port: '',
    },
    boiteVocale: detail.voicemail
      ? [
          {
            label: 'Statut',
            value: !detail.voicemail.enabled
              ? 'Désactivée'
              : detail.voicemail.announcementOnly
              ? 'Annonce seule'
              : 'Activée',
          },
          {
            label: 'Langue',
            value:
              detail.voicemail.language === 'fr' ? 'Français' : detail.voicemail.language ?? '',
          },
          {
            label: 'Adresse E-Mail',
            value: detail.voicemail.email ?? 'Définir un E-mail',
            link: !detail.voicemail.email,
          },
          {label: 'Mot de passe', value: 'Voir', link: true},
        ]
      : boiteVocaleParDefaut(),
    appelsEntrants: detail.inboundCalls
      ? [
          redirectEnLigne("Redirection en cas d'absence", detail.inboundCalls.unavailableRedirect),
          redirectEnLigne('Redirection si occupé', detail.inboundCalls.busyRedirect),
          redirectEnLigne(
            'Redirection inconditionnelle',
            detail.inboundCalls.unconditionalRedirect
          ),
          detail.inboundCalls.backupNumber
            ? {
                label: 'Redirection de Backup',
                value:
                  detail.inboundCalls.backupNumber === 'voicemail'
                    ? 'Boîte vocale'
                    : detail.inboundCalls.backupNumber,
              }
            : {label: 'Redirection de Backup', value: 'Non', danger: true},
        ]
      : appelsEntrantsParDefaut(),
    limitations: limitationsParDefaut(),
    numeroAffiche: detail.outboundCalls?.callerIdHidden
      ? 'Masqué'
      : detail.outboundCalls?.callerIdNumber ?? undefined,
    detail,
  };
};

/** Détail d'un numéro (écran « Gérer vos numéros » > loupe), rafraîchi à chaque consultation. */
const getNumeroDetail = async (serviceId: number, numeroId: number): Promise<TrunkNumero> => {
  const [response, locations] = await Promise.all([
    axios.get<ApiNumberDetail>(`/api/services/telephonie/${serviceId}/number/${numeroId}`),
    fetchLocations(serviceId),
  ]);
  const locationsParId = new Map(locations.map((location) => [location.id, location]));

  return {...trunkNumeroDepuisDetailApi(response.data, locationsParId), numeroId};
};

interface UpdateNumeroPayload {
  locationId?: number;
  sipAccount?: {password?: string; generateNewPassword?: boolean};
  voicemail?: {
    enabled?: boolean;
    announcementOnly?: boolean;
    keepOnServer?: boolean;
    email?: string;
    language?: string;
    password?: string;
    generateNewPassword?: boolean;
  };
  inboundCalls?: {
    unavailableRedirect?: CallRedirectInfo;
    noAnswerTimeout?: number;
    busyRedirect?: CallRedirectInfo;
    unconditionalRedirect?: CallRedirectInfo;
    backupNumber?: string;
    backupEnabled?: boolean;
    parallelRingingEnabled?: boolean;
    parallelRingingNumber?: string;
    musicOnHold?: boolean;
    rejectAnonymousCalls?: boolean;
    rejectForwardedCalls?: boolean;
    callWaiting?: boolean;
  };
  outboundCalls?: {callerIdHidden?: boolean; callerIdNumber?: string};
}

/** Modification d'un numéro (localisation, mot de passe SIP) — persistée via api-telephonie. */
const updateNumero = async (
  serviceId: number,
  numeroId: number,
  payload: UpdateNumeroPayload
): Promise<TrunkNumero> => {
  const [response, locations] = await Promise.all([
    axios.put<ApiNumberDetail>(`/api/services/telephonie/${serviceId}/number/${numeroId}`, payload),
    fetchLocations(serviceId),
  ]);
  const locationsParId = new Map(locations.map((location) => [location.id, location]));

  return {...trunkNumeroDepuisDetailApi(response.data, locationsParId), numeroId};
};

/** Révèle le mot de passe SIP ou softphone actuel d'un numéro (lecture à la demande, jamais mise en cache). */
const getNumeroPassword = async (
  serviceId: number,
  numeroId: number,
  target: 'sip' | 'softphone'
): Promise<string> => {
  const response = await axios.get<{password: string}>(
    `/api/services/telephonie/${serviceId}/number/${numeroId}/password`,
    {params: {target}}
  );

  return response.data.password;
};

export interface ApiVoicemailGreeting {
  serviceId: number;
  numberId: number;
  /** Base64 (wav ou mp3) ; `null` si aucune annonce personnalisée n'est enregistrée. */
  audioContent: string | null;
}

const getVoicemailGreeting = async (
  serviceId: number,
  numeroId: number
): Promise<ApiVoicemailGreeting> => {
  const response = await axios.get<ApiVoicemailGreeting>(
    `/api/services/telephonie/${serviceId}/number/${numeroId}/voicemail/greeting`
  );

  return response.data;
};

const updateVoicemailGreeting = async (
  serviceId: number,
  numeroId: number,
  audioContent: string
): Promise<ApiVoicemailGreeting> => {
  const response = await axios.put<ApiVoicemailGreeting>(
    `/api/services/telephonie/${serviceId}/number/${numeroId}/voicemail/greeting`,
    {audioContent}
  );

  return response.data;
};

export interface ApiVoicemailMessage {
  id: number;
  serviceId: number;
  numberId: number;
  incoming: boolean | null;
  type: string | null;
  /** 'NEW' ou 'SEEN', tel que relayé par la plateforme. */
  flag: string | null;
  createdOn: string | null;
  peerName: string | null;
  peerUri: string | null;
  /** Secondes. */
  duration: number | null;
  pages: number | null;
}

export interface ApiVoicemailMessageAudio {
  serviceId: number;
  numberId: number;
  messageId: number;
  /** Base64 (wav). */
  audioContent: string | null;
}

const getVoicemailMessages = async (
  serviceId: number,
  numeroId: number
): Promise<ApiVoicemailMessage[]> => {
  const response = await axios.get<ApiVoicemailMessage[]>(
    `/api/services/telephonie/${serviceId}/number/${numeroId}/voicemail/messages`
  );

  return response.data;
};

const getVoicemailMessageAudio = async (
  serviceId: number,
  numeroId: number,
  messageId: number
): Promise<ApiVoicemailMessageAudio> => {
  const response = await axios.get<ApiVoicemailMessageAudio>(
    `/api/services/telephonie/${serviceId}/number/${numeroId}/voicemail/message/${messageId}/audio`
  );

  return response.data;
};

const deleteVoicemailMessage = async (
  serviceId: number,
  numeroId: number,
  messageId: number
): Promise<void> => {
  await axios.delete(
    `/api/services/telephonie/${serviceId}/number/${numeroId}/voicemail/message/${messageId}`
  );
};

export interface TeamsRecord {
  txtRecord: string;
}

const getTeamsRecord = async (): Promise<TeamsRecord> => {
  return Promise.resolve({txtRecord: 'MS=ms12345678'});
};

const saveTeamsRecord = async (txtRecord: string): Promise<TeamsRecord> => {
  return Promise.resolve({txtRecord});
};

export interface ConferenceRooms {
  salles: SalleConference[];
}

const getConferenceRooms = async (): Promise<ConferenceRooms> => {
  return Promise.resolve({
    salles: [
      {
        id: 1,
        nom: 'Salle Direction',
        numero: '021 566 10 20',
        codeAdmin: '4521',
        codeUtilisateur: '8734',
      },
      {
        id: 2,
        nom: 'Salle Commerciale',
        numero: '021 566 10 21',
        codeAdmin: '2298',
        codeUtilisateur: '1456',
      },
    ],
  });
};

const modifierSalleConference = async (
  id: number,
  nom: string
): Promise<{id: number; nom: string}> => {
  return Promise.resolve({id, nom});
};

const getVoiceMailConfig = async (): Promise<ConfigurationBoiteVocale> => {
  return Promise.resolve({
    statut: 'actif',
    nomEnregistrement: 'accueil_standard.wav',
    langue: 'fr',
    email: 'contact@celeste.fr',
    conserverMessages: true,
    codeAcces: '3391',
  });
};

const saveVoiceMailConfig = async (
  configuration: ConfigurationBoiteVocale
): Promise<ConfigurationBoiteVocale> => {
  return Promise.resolve(configuration);
};

const SONS_PAR_TYPE: Record<TypeSon, FichierSon[]> = {
  musique_attente: [
    {id: 1, titre: 'Musique par défaut', actif: true, parDefaut: true},
    {id: 2, titre: 'Jazz doux'},
    {id: 3, titre: 'Musique classique'},
  ],
  tonalite: [
    {id: 1, titre: 'Tonalité par défaut', parDefaut: true},
    {id: 2, titre: 'Sonnerie standard'},
  ],
  annonce_fichier: [
    {id: 1, titre: 'Bienvenue'},
    {id: 2, titre: 'Fermeture annuelle'},
  ],
  tts: [
    {id: 1, titre: 'Accueil FR', langue: 'fr', voix: 'femme', texte: 'Bienvenue chez Celeste.'},
  ],
};

const getSons = async (type: TypeSon): Promise<FichierSon[]> => {
  return Promise.resolve(SONS_PAR_TYPE[type]);
};

export interface AccessCodeStatus {
  actif: boolean;
}

const getAccessCodeStatus = async (): Promise<AccessCodeStatus> => {
  return Promise.resolve({actif: false});
};

const getUtilisateursNumeroCourtInvalide = async (): Promise<UtilisateurNumeroCourtInvalide[]> => {
  return Promise.resolve([
    {
      id: 1,
      nom: 'Dupont',
      prenom: 'Jean',
      telephone: '021 566 10 01',
      numeroCourtActuel: '15',
      numeroCourtSuggere: '2015',
    },
    {
      id: 2,
      nom: 'Lambert',
      prenom: 'Marie',
      telephone: '022 566 79 34',
      numeroCourtActuel: '99',
      numeroCourtSuggere: '2099',
    },
  ]);
};

const changerAccessCode = async (
  actif: boolean,
  changements: {id: number; numeroCourt: string}[]
): Promise<{actif: boolean; changements: {id: number; numeroCourt: string}[]}> => {
  return Promise.resolve({actif, changements});
};

const getClirAssignations = async (): Promise<AssignationClir[]> => {
  return Promise.resolve([
    {telephone: '021 566 10 01', numeroAffiche: '021 568 78 05'},
    {telephone: '022 566 79 34', numeroAffiche: '021 568 78 05'},
    {telephone: '022 566 79 37', numeroAffiche: null},
    {telephone: '022 566 79 38', numeroAffiche: null},
    {telephone: '024 566 70 07', numeroAffiche: null},
  ]);
};

const getTelephonesIp = async (): Promise<MaterielIp[]> => {
  return Promise.resolve([
    {
      id: 1,
      marque: 'Yealink',
      modele: 'T54W',
      mac: 'AA:BB:CC:11:22:33',
      prestationId: 'CTX-1001',
      statut: 'associe',
      isIad: false,
      isCsta: false,
      isSoftphone: false,
      isIpiu: false,
      canDelete: true,
      canEdit: false,
      nbCompteMax: 1,
      comptes: [
        {
          idWhitelist: 1,
          numero: '0215687805',
          numeroFormate: '021 568 78 05',
          idContact: 1,
          nomContact: 'Jean Dupont',
        },
      ],
      guideUrl: 'https://support.celeste.fr/guides/yealink-t54w.pdf',
    },
    {
      id: 2,
      marque: 'Yealink',
      modele: 'T46U',
      mac: 'AA:BB:CC:11:22:34',
      prestationId: 'CTX-1001',
      statut: 'associe',
      isIad: false,
      isCsta: false,
      isSoftphone: false,
      isIpiu: false,
      canDelete: true,
      canEdit: false,
      nbCompteMax: 1,
      comptes: [
        {
          idWhitelist: 2,
          numero: '0225667934',
          numeroFormate: '022 566 79 34',
          idContact: 0,
        },
      ],
    },
    {
      id: 3,
      marque: 'Softphone Celeste',
      modele: 'Bria',
      prestationId: 'CTX-1001',
      statut: 'associe',
      isIad: false,
      isCsta: true,
      isSoftphone: true,
      isIpiu: false,
      canDelete: false,
      canEdit: true,
      nbCompteMax: 1,
      linkedIdMateriel: 1,
      comptes: [
        {
          idWhitelist: 3,
          numero: '0215687805',
          numeroFormate: '021 568 78 05',
          idContact: 1,
          nomContact: 'Jean Dupont',
        },
      ],
    },
    {
      id: 4,
      marque: 'Yealink',
      modele: 'W56H Combiné',
      numeroSerie: 'IPUI-0012345',
      prestationId: 'CTX-1001',
      statut: 'associe',
      isIad: false,
      isCsta: false,
      isSoftphone: false,
      isIpiu: true,
      canDelete: true,
      canEdit: false,
      nbCompteMax: 1,
      comptes: [
        {
          idWhitelist: 4,
          numero: '0225667937',
          numeroFormate: '022 566 79 37',
          idContact: 2,
          nomContact: 'Marie Lambert',
          combineIpiu: 'Combiné 1',
        },
      ],
    },
    {
      id: 5,
      marque: 'Poly',
      modele: 'VVX250',
      mac: 'AA:BB:CC:11:22:35',
      prestationId: 'CTX-1001',
      statut: 'disponible',
      isIad: false,
      isCsta: false,
      isSoftphone: false,
      isIpiu: false,
      canDelete: true,
      canEdit: false,
      nbCompteMax: 1,
      comptes: [],
    },
    {
      id: 6,
      marque: 'Patton',
      modele: 'SmartNode SN4114 (IAD 4 comptes)',
      mac: 'AA:BB:CC:11:22:36',
      prestationId: 'CTX-1001',
      statut: 'multicompte',
      isIad: true,
      isCsta: false,
      isSoftphone: false,
      isIpiu: false,
      canDelete: true,
      canEdit: false,
      nbCompteMax: 4,
      comptes: [
        {
          idWhitelist: 5,
          numero: '0245667980',
          numeroFormate: '024 566 70 80',
          idContact: 3,
          nomContact: 'Bertrand Pierre-Louis',
        },
        {
          idWhitelist: 6,
          numero: '0245667981',
          numeroFormate: '024 566 70 81',
          idContact: 0,
        },
      ],
    },
    {
      id: 7,
      marque: 'Snom',
      modele: 'D717',
      mac: 'AA:BB:CC:11:22:37',
      prestationId: 'CTX-1001',
      statut: 'manuel',
      isIad: false,
      isCsta: false,
      isSoftphone: false,
      isIpiu: false,
      canDelete: true,
      canEdit: false,
      nbCompteMax: 1,
      comptes: [],
    },
    {
      id: 8,
      marque: 'Cisco',
      modele: 'ATA 191 (passerelle analogique héritée)',
      mac: 'AA:BB:CC:11:22:38',
      prestationId: 'CTX-1001',
      statut: 'autre',
      isIad: false,
      isCsta: false,
      isSoftphone: false,
      isIpiu: false,
      canDelete: false,
      canEdit: false,
      nbCompteMax: 0,
      comptes: [],
    },
    {
      id: 9,
      marque: 'Softphone Celeste',
      modele: 'Bria (non associé)',
      prestationId: 'CTX-1001',
      statut: 'disponible',
      isIad: false,
      isCsta: true,
      isSoftphone: true,
      isIpiu: false,
      canDelete: false,
      canEdit: true,
      nbCompteMax: 1,
      comptes: [],
    },
  ]);
};

const getModelesMateriel = async (): Promise<ModeleMateriel[]> => {
  return Promise.resolve([
    {
      id: 1,
      marque: 'Yealink',
      modele: 'T54W',
      categorie: 'standard',
      firmwareMinimum: '96.86.0.15',
    },
    {id: 2, marque: 'Yealink', modele: 'T46U', categorie: 'standard', firmwareMinimum: '96.85.0.5'},
    {id: 3, marque: 'Poly', modele: 'VVX250', categorie: 'standard', firmwareMinimum: '5.9.6'},
    {id: 4, marque: 'Snom', modele: 'D717', categorie: 'standard', firmwareMinimum: '10.1.90'},
    {
      id: 5,
      marque: 'Yealink',
      modele: 'W56H Combiné',
      categorie: 'dect',
      firmwareMinimum: '90.85.0.15',
    },
  ]);
};

/** VoIP.asmx/GetToIPFreeNumber — numéros câblables mais non encore associés à un matériel. */
const getToIpFreeNumbers = async (): Promise<string[]> => {
  return Promise.resolve(['021 566 10 05', '021 566 10 06', '022 566 79 40', '024 566 70 10']);
};

/** VoIP.asmx/GetFreeContacts — contacts utilisateur proposables lors d'une association. */
const getToIpFreeContacts = async (): Promise<ContactToIp[]> => {
  return Promise.resolve([
    {id: 1, nom: 'Jean Dupont'},
    {id: 2, nom: 'Marie Lambert'},
    {id: 3, nom: 'Bertrand Pierre-Louis'},
    {id: 4, nom: 'Frederic Jean-Pierre'},
    {id: 5, nom: 'Sophie Martin'},
  ]);
};

const getSipsSrtpStatut = async (): Promise<SipsSrtpStatut> => {
  return Promise.resolve({disponible: true, actif: false});
};

const saveSipsSrtpStatut = async (actif: boolean): Promise<SipsSrtpStatut> => {
  return Promise.resolve({disponible: true, actif});
};

const getHistoriqueAppels = async (): Promise<AppelHistorique[]> => {
  return Promise.resolve([
    {
      id: 1,
      dateHeure: '2026-08-27T10:43:16',
      type: 'entrant',
      appelant: '57884356',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 202,
    },
    {
      id: 2,
      dateHeure: '2026-08-27T10:37:26',
      type: 'entrant',
      appelant: '57743941',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 38,
    },
    {
      id: 3,
      dateHeure: '2026-08-27T10:08:57',
      type: 'entrant',
      appelant: '4041510',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 198,
    },
    {
      id: 4,
      dateHeure: '2026-08-27T10:05:01',
      type: 'sortant',
      appelant: '4210',
      destination: ['002304041510'],
      resultat: 'reussi',
      dureeSecondes: 38,
    },
    {
      id: 5,
      dateHeure: '2026-08-27T09:31:39',
      type: 'entrant',
      appelant: '6600004',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 210,
    },
    {
      id: 6,
      dateHeure: '2026-08-27T09:23:01',
      type: 'sortant',
      appelant: '6595010',
      destination: ['841', 'ipbx/ipbxV2.php'],
      resultat: 'abandonne',
      dureeSecondes: 40,
    },
    {
      id: 7,
      dateHeure: '2026-08-27T09:02:21',
      type: 'sortant',
      appelant: '4208',
      destination: ['0023054521655'],
      resultat: 'abandonne',
      dureeSecondes: 0,
    },
    {
      id: 8,
      dateHeure: '2026-08-27T08:57:21',
      type: 'entrant',
      appelant: '54521655',
      destination: ['841', '4208'],
      resultat: 'reussi',
      dureeSecondes: 548,
    },
    {
      id: 9,
      dateHeure: '2026-08-27T08:55:23',
      type: 'sortant',
      appelant: '4208',
      destination: ['0023054521655'],
      resultat: 'abandonne',
      dureeSecondes: 0,
    },
    {
      id: 10,
      dateHeure: '2026-08-27T08:46:34',
      type: 'entrant',
      appelant: '4041510',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 140,
    },
    {
      id: 11,
      dateHeure: '2026-08-27T08:31:12',
      type: 'entrant',
      appelant: '57884356',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 95,
    },
    {
      id: 12,
      dateHeure: '2026-08-27T08:20:45',
      type: 'sortant',
      appelant: '4208',
      destination: ['0023054521655'],
      resultat: 'reussi',
      dureeSecondes: 87,
    },
    {
      id: 13,
      dateHeure: '2026-08-27T08:12:03',
      type: 'entrant',
      appelant: '54521655',
      destination: ['841', '4208'],
      resultat: 'reussi',
      dureeSecondes: 312,
    },
    {
      id: 14,
      dateHeure: '2026-08-27T07:58:29',
      type: 'sortant',
      appelant: '6595010',
      destination: ['841', 'ipbx/ipbxV2.php'],
      resultat: 'abandonne',
      dureeSecondes: 0,
    },
    {
      id: 15,
      dateHeure: '2026-08-27T07:41:17',
      type: 'entrant',
      appelant: '4041510',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 176,
    },
    {
      id: 16,
      dateHeure: '2026-08-26T19:22:08',
      type: 'sortant',
      appelant: '4210',
      destination: ['002304041510'],
      resultat: 'reussi',
      dureeSecondes: 64,
    },
    {
      id: 17,
      dateHeure: '2026-08-26T18:55:41',
      type: 'entrant',
      appelant: '57743941',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 22,
    },
    {
      id: 18,
      dateHeure: '2026-08-26T18:30:19',
      type: 'sortant',
      appelant: '4208',
      destination: ['0023054521655'],
      resultat: 'abandonne',
      dureeSecondes: 0,
    },
    {
      id: 19,
      dateHeure: '2026-08-26T17:48:53',
      type: 'entrant',
      appelant: '6600004',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 288,
    },
    {
      id: 20,
      dateHeure: '2026-08-26T17:12:36',
      type: 'entrant',
      appelant: '54521655',
      destination: ['841', '4208'],
      resultat: 'reussi',
      dureeSecondes: 411,
    },
    {
      id: 21,
      dateHeure: '2026-08-26T16:44:07',
      type: 'sortant',
      appelant: '6595010',
      destination: ['841', 'ipbx/ipbxV2.php'],
      resultat: 'abandonne',
      dureeSecondes: 40,
    },
    {
      id: 22,
      dateHeure: '2026-08-26T16:09:52',
      type: 'entrant',
      appelant: '57884356',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 133,
    },
    {
      id: 23,
      dateHeure: '2026-08-26T15:37:28',
      type: 'sortant',
      appelant: '4210',
      destination: ['002304041510'],
      resultat: 'reussi',
      dureeSecondes: 51,
    },
    {
      id: 24,
      dateHeure: '2026-08-26T15:02:14',
      type: 'entrant',
      appelant: '4041510',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 199,
    },
    {
      id: 25,
      dateHeure: '2026-08-26T14:33:49',
      type: 'sortant',
      appelant: '4208',
      destination: ['0023054521655'],
      resultat: 'reussi',
      dureeSecondes: 76,
    },
    {
      id: 26,
      dateHeure: '2026-08-26T14:01:22',
      type: 'entrant',
      appelant: '57743941',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 45,
    },
    {
      id: 27,
      dateHeure: '2026-08-26T13:28:07',
      type: 'entrant',
      appelant: '54521655',
      destination: ['841', '4208'],
      resultat: 'reussi',
      dureeSecondes: 264,
    },
    {
      id: 28,
      dateHeure: '2026-08-26T12:55:31',
      type: 'sortant',
      appelant: '6595010',
      destination: ['841', 'ipbx/ipbxV2.php'],
      resultat: 'abandonne',
      dureeSecondes: 0,
    },
    {
      id: 29,
      dateHeure: '2026-08-26T12:20:44',
      type: 'entrant',
      appelant: '6600004',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 302,
    },
    {
      id: 30,
      dateHeure: '2026-08-26T11:48:18',
      type: 'sortant',
      appelant: '4210',
      destination: ['002304041510'],
      resultat: 'reussi',
      dureeSecondes: 29,
    },
    {
      id: 31,
      dateHeure: '2026-08-26T11:15:53',
      type: 'entrant',
      appelant: '57884356',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 118,
    },
    {
      id: 32,
      dateHeure: '2026-08-26T10:42:27',
      type: 'sortant',
      appelant: '4208',
      destination: ['0023054521655'],
      resultat: 'abandonne',
      dureeSecondes: 0,
    },
    {
      id: 33,
      dateHeure: '2026-08-26T10:09:36',
      type: 'entrant',
      appelant: '4041510',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 154,
    },
    {
      id: 34,
      dateHeure: '2026-08-26T09:37:12',
      type: 'entrant',
      appelant: '54521655',
      destination: ['841', '4208'],
      resultat: 'reussi',
      dureeSecondes: 372,
    },
    {
      id: 35,
      dateHeure: '2026-08-26T09:04:49',
      type: 'sortant',
      appelant: '6595010',
      destination: ['841', 'ipbx/ipbxV2.php'],
      resultat: 'abandonne',
      dureeSecondes: 40,
    },
    {
      id: 36,
      dateHeure: '2026-08-26T08:33:15',
      type: 'entrant',
      appelant: '57743941',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 61,
    },
    {
      id: 37,
      dateHeure: '2026-08-26T08:02:38',
      type: 'sortant',
      appelant: '4210',
      destination: ['002304041510'],
      resultat: 'reussi',
      dureeSecondes: 47,
    },
    {
      id: 38,
      dateHeure: '2026-08-26T07:41:04',
      type: 'entrant',
      appelant: '6600004',
      destination: ['840', '4210'],
      resultat: 'reussi',
      dureeSecondes: 226,
    },
    {
      id: 39,
      dateHeure: '2026-08-26T07:15:52',
      type: 'sortant',
      appelant: '4208',
      destination: ['0023054521655'],
      resultat: 'abandonne',
      dureeSecondes: 0,
    },
  ]);
};

/** Forme brute de la ressource `BusinessTrunkConfig` d'api-telephonie (voir docs OpenAPI). */
interface ApiBusinessTrunkConfig {
  equipmentModel: string | null;
  redundancyStrategy:
    | 'REDUNDANT'
    | 'LOAD_BALANCING'
    | 'REDUNDANT_NAT'
    | 'LOAD_BALANCING_NAT'
    | null;
  redirectEnabled: boolean | null;
  referEnabled: boolean | null;
  mediaHoldFormat: 'SEND_ONLY' | 'INACTIVE' | 'SEND_RECV' | null;
  staticRegistrationEnabled: boolean | null;
  callHistoryHeader: 'NONE' | 'CALL_HISTORY_INFO' | 'CALL_DIVERSION_INFO' | null;
  sendRemotePartyInfo: boolean | null;
  usePAssertedId: boolean | null;
  destinationAddressIsToHeader: boolean | null;
  ownDomainNameInHostparts: boolean | null;
  fromPrefix: 'NO_CHANGE' | 'INTERNATIONAL' | 'NATIONAL' | 'E164' | 'NONE' | null;
  fromDisplayName: 'NO' | 'YES' | 'NUMBER_ONLY' | 'FROM_USER_PART' | null;
  toPrefix: 'NO_CHANGE' | 'INTERNATIONAL' | 'NATIONAL' | 'E164' | 'NONE' | null;
  userPhone: boolean | null;
  numberFormat: 'ACCESS_CODE' | 'INTERNATIONAL' | null;
  allowOutCalls: boolean | null;
  allowDirectInCalls: boolean | null;
  enableMsTeams: boolean | null;
  transportMethod: 'UDP' | 'StaticTCP' | 'StaticUDP' | null;
  /** Plafond d'appels simultanés — lecture seule, jamais réécrit par l'écran legacy. */
  maxCalls: number | null;
  stripDigits: number | null;
  additionalToPrefix: string | null;
}

const REDUNDANCY_MODE_PAR_API: Record<string, string> = {
  REDUNDANT: 'Redondant',
  LOAD_BALANCING: 'Répartition de charge',
  REDUNDANT_NAT: 'Redondant NAT',
  LOAD_BALANCING_NAT: 'Répartition de charge NAT',
};
const REDUNDANCY_MODE_VERS_API: Record<string, string> = {
  Redondant: 'REDUNDANT',
  'Répartition de charge': 'LOAD_BALANCING',
  'Redondant NAT': 'REDUNDANT_NAT',
  'Répartition de charge NAT': 'LOAD_BALANCING_NAT',
};

const MEDIA_HOLD_FORMAT_PAR_API: Record<string, string> = {
  SEND_ONLY: 'Envoi seulement',
  INACTIVE: 'Inactif',
  SEND_RECV: 'Envoi/réception',
};
const MEDIA_HOLD_FORMAT_VERS_API: Record<string, string> = {
  'Envoi seulement': 'SEND_ONLY',
  Inactif: 'INACTIVE',
  'Envoi/réception': 'SEND_RECV',
};

const CALL_HISTORY_HEADER_PAR_API: Record<string, string> = {
  NONE: 'Aucun',
  CALL_HISTORY_INFO: 'Call-History-Info',
  CALL_DIVERSION_INFO: 'Call-Diversion-Info',
};
const CALL_HISTORY_HEADER_VERS_API: Record<string, string> = {
  Aucun: 'NONE',
  'Call-History-Info': 'CALL_HISTORY_INFO',
  'Call-Diversion-Info': 'CALL_DIVERSION_INFO',
};

/** Partagée par « Préfixe d'adresse de destination » (toPrefix) et « appelante » (fromPrefix). */
const ADDRESS_PREFIX_PAR_API: Record<string, string> = {
  NO_CHANGE: 'Ne pas modifier',
  INTERNATIONAL: 'International',
  NATIONAL: 'National',
  E164: 'Préfixe E164',
  NONE: 'Sans préfixe',
};
const ADDRESS_PREFIX_VERS_API: Record<string, string> = {
  'Ne pas modifier': 'NO_CHANGE',
  International: 'INTERNATIONAL',
  National: 'NATIONAL',
  'Préfixe E164': 'E164',
  'Sans préfixe': 'NONE',
};

const ouiNonPar = (valeur: boolean | null): string => (valeur ? 'Oui' : 'Non');
const ouiNonVers = (valeur: string): boolean => valeur === 'Oui';
const ouiParDefautNonPar = (valeur: boolean | null): string =>
  valeur ? 'Oui (par défaut)' : 'Non';
const ouiParDefautNonVers = (valeur: string): boolean => valeur === 'Oui (par défaut)';

/**
 * `sendRemotePartyInfo`, `usePAssertedId`, `destinationAddressIsToHeader`,
 * `fromDisplayName`, `numberFormat`, `enableMsTeams` : requis par le `PUT`
 * mais sans contrôle dans ce formulaire (pas d'équivalent sur l'écran legacy
 * actuel) — chargés depuis l'API et renvoyés tels quels à l'enregistrement.
 */
const businessTrunkConfigDepuisApi = (c: ApiBusinessTrunkConfig): ConfigurationTrunkSip => ({
  modelTypeVersion: c.equipmentModel ?? '',
  redundancyMode: REDUNDANCY_MODE_PAR_API[c.redundancyStrategy ?? ''] ?? 'Redondant',
  enableRedirect: ouiNonPar(c.redirectEnabled),
  enableRefer: ouiNonPar(c.referEnabled),
  mediaHoldFormat: MEDIA_HOLD_FORMAT_PAR_API[c.mediaHoldFormat ?? ''] ?? 'Envoi seulement',
  authenticateBusinessTrunkUser: ouiNonPar(c.staticRegistrationEnabled),
  transportMethod: c.transportMethod ?? 'UDP',
  callHistory: CALL_HISTORY_HEADER_PAR_API[c.callHistoryHeader ?? ''] ?? 'Aucun',
  ownDomainNameInHostparts: ouiNonPar(c.ownDomainNameInHostparts),
  destinationAddressPrefix: ADDRESS_PREFIX_PAR_API[c.toPrefix ?? ''] ?? 'Ne pas modifier',
  callingAddressPrefix: ADDRESS_PREFIX_PAR_API[c.fromPrefix ?? ''] ?? 'Ne pas modifier',
  stripLeadingDigits: c.stripDigits ?? 0,
  additionalDestinationAddressPrefix: c.additionalToPrefix ?? '',
  userPhone: ouiNonPar(c.userPhone),
  allowOutgoingCalls: ouiParDefautNonPar(c.allowOutCalls),
  allowIncomingDirectCallsToVoipNumbers: ouiParDefautNonPar(c.allowDirectInCalls),
  concurrentCalls: c.maxCalls ?? 0,
  sendRemotePartyInfo: c.sendRemotePartyInfo ?? false,
  usePAssertedId: c.usePAssertedId ?? false,
  destinationAddressIsToHeader: c.destinationAddressIsToHeader ?? false,
  fromDisplayName: c.fromDisplayName ?? 'NO',
  numberFormat: c.numberFormat ?? 'ACCESS_CODE',
  enableMsTeams: c.enableMsTeams ?? false,
});

/** `maxCalls` volontairement absent : lecture seule côté API. */
const businessTrunkConfigVersApi = (
  c: ConfigurationTrunkSip
): Omit<ApiBusinessTrunkConfig, 'maxCalls'> => ({
  equipmentModel: c.modelTypeVersion,
  redundancyStrategy: (REDUNDANCY_MODE_VERS_API[c.redundancyMode] ??
    'REDUNDANT') as ApiBusinessTrunkConfig['redundancyStrategy'],
  redirectEnabled: ouiNonVers(c.enableRedirect),
  referEnabled: ouiNonVers(c.enableRefer),
  mediaHoldFormat: (MEDIA_HOLD_FORMAT_VERS_API[c.mediaHoldFormat] ??
    'SEND_ONLY') as ApiBusinessTrunkConfig['mediaHoldFormat'],
  staticRegistrationEnabled: ouiNonVers(c.authenticateBusinessTrunkUser),
  callHistoryHeader: (CALL_HISTORY_HEADER_VERS_API[c.callHistory] ??
    'NONE') as ApiBusinessTrunkConfig['callHistoryHeader'],
  sendRemotePartyInfo: c.sendRemotePartyInfo,
  usePAssertedId: c.usePAssertedId,
  destinationAddressIsToHeader: c.destinationAddressIsToHeader,
  ownDomainNameInHostparts: ouiNonVers(c.ownDomainNameInHostparts),
  fromPrefix: (ADDRESS_PREFIX_VERS_API[c.callingAddressPrefix] ??
    'NO_CHANGE') as ApiBusinessTrunkConfig['fromPrefix'],
  fromDisplayName: c.fromDisplayName as ApiBusinessTrunkConfig['fromDisplayName'],
  toPrefix: (ADDRESS_PREFIX_VERS_API[c.destinationAddressPrefix] ??
    'NO_CHANGE') as ApiBusinessTrunkConfig['toPrefix'],
  stripDigits: c.stripLeadingDigits,
  additionalToPrefix: c.additionalDestinationAddressPrefix,
  userPhone: ouiNonVers(c.userPhone),
  numberFormat: c.numberFormat as ApiBusinessTrunkConfig['numberFormat'],
  allowOutCalls: ouiParDefautNonVers(c.allowOutgoingCalls),
  allowDirectInCalls: ouiParDefautNonVers(c.allowIncomingDirectCallsToVoipNumbers),
  enableMsTeams: c.enableMsTeams,
  transportMethod: c.transportMethod as ApiBusinessTrunkConfig['transportMethod'],
});

/** « Configurer mon Trunk SIP » — configuration technique du trunk (SBC/PBX partenaire). */
const getConfigurationTrunkSip = async (
  serviceId: number,
  numeroId: number
): Promise<ConfigurationTrunkSip> => {
  const response = await axios.get<ApiBusinessTrunkConfig>(
    `/api/services/telephonie/${serviceId}/number/${numeroId}/business-trunk-config`
  );
  return businessTrunkConfigDepuisApi(response.data);
};

const saveConfigurationTrunkSip = async (
  serviceId: number,
  numeroId: number,
  configuration: ConfigurationTrunkSip
): Promise<ConfigurationTrunkSip> => {
  const response = await axios.put<ApiBusinessTrunkConfig>(
    `/api/services/telephonie/${serviceId}/number/${numeroId}/business-trunk-config`,
    businessTrunkConfigVersApi(configuration)
  );
  return businessTrunkConfigDepuisApi(response.data);
};

export {
  getSuiviConsommationTelHD,
  getTrunkSipConsommation,
  getCallsPage,
  getAnnuaire,
  getAnnuaireInterne,
  getPbxGroupes,
  getPlagesHoraires,
  getIvrs,
  getBlacklistEntrants,
  ajouterBlacklistEntrant,
  supprimerBlacklistEntrant,
  getVirtualNumbers,
  getTicketsAppelsEntrants,
  getConfigurationAntiSpamAppel,
  getLimitation,
  putLimitation,
  getAuthorizationOverrides,
  putAuthorizationOverrides,
  getIpRestriction,
  putIpRestriction,
  getNumberIpRestriction,
  putNumberIpRestriction,
  getLocalisationsUrgence,
  ajouterLocalisationUrgence,
  modifierLocalisationUrgence,
  supprimerLocalisationUrgence,
  searchAddresses,
  getSwissBuildingReference,
  getTrunkNumeros,
  getNumeroDetail,
  updateNumero,
  getNumeroPassword,
  getVoicemailGreeting,
  updateVoicemailGreeting,
  getVoicemailMessages,
  getVoicemailMessageAudio,
  deleteVoicemailMessage,
  getTelephonieServices,
  getVirtualPbxNumeros,
  getVirtualPbxActivite,
  getTeamsRecord,
  saveTeamsRecord,
  getConferenceRooms,
  modifierSalleConference,
  getVoiceMailConfig,
  saveVoiceMailConfig,
  getSons,
  getAccessCodeStatus,
  getUtilisateursNumeroCourtInvalide,
  changerAccessCode,
  getClirAssignations,
  getTelephonesIp,
  getModelesMateriel,
  getToIpFreeNumbers,
  getToIpFreeContacts,
  getSipsSrtpStatut,
  saveSipsSrtpStatut,
  getHistoriqueAppels,
  getConfigurationTrunkSip,
  saveConfigurationTrunkSip,
};
